/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";

import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import SearchInput from "components/SearchInput";

import { useSnackbar } from "hooks/useSnackbar";

import configData from "config/config.json";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { navigateFromRow, opacity2hex } from "utils/miscUtils";
import {
  listSchedules,
  toggleSchedule,
  deleteSchedule,
} from "services/reportSchedulerService";
import RunHistoryModal from "./components/RunHistoryModal";
import TestReportModal from "./components/TestReportModal";
import { formatInTimeZone } from "date-fns-tz";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";

const useStyles = makeStyles((theme: any) => ({
  clickableRow: {
    "&:hover > td": {
      backgroundColor:
        theme.palette.secondary.light + opacity2hex("0.20") + " !important",
      transition: "background-color 120ms ease-in-out",
    },
  },
  rowLink: { color: "inherit", textDecoration: "none" },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  inactiveChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
}));

const REPORT_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "daily_summary", label: "Daily Summary" },
  { value: "transactions", label: "Transactions" },
];

const formatReportType = (
  reports: Array<{ type: string; enabled?: boolean }>,
) => {
  if (!reports?.length) return "-";
  const enabled = reports.filter((r) => r.enabled !== false);
  if (!enabled.length) return "-";
  return enabled
    .map((r) => (r.type === "daily_summary" ? "Daily Summary" : "Transactions"))
    .join(", ");
};

const formatFrequency = (schedule: any) => {
  if (schedule.frequency === "hourly")
    return `Every ${schedule.hourlyInterval} hour(s)`;
  return (
    schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)
  );
};

const formatEmails = (emails: string[]) => {
  if (!emails?.length) return "-";
  if (emails.length <= 2) return emails.join(", ");
  return `${emails.slice(0, 2).join(", ")} +${emails.length - 2} more`;
};

const formatLastRun = (date?: string) => {
  if (!date) return "Never";
  return formatInTimeZone(new Date(date), "UTC", "dd MMM yyyy HH:mm:ss 'UTC'");
};

const ReportSchedulers = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalSchedules, setTotalSchedules] = useState(0);
  const [search, setSearch] = useState("");
  const [reportTypeFilter, setReportTypeFilter] = useState("");

  // Modal state - modals will be created in Task 16
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testScheduleId, setTestScheduleId] = useState<string | null>(null);
  const [runHistoryModalOpen, setRunHistoryModalOpen] = useState(false);
  const [runHistoryScheduleId, setRunHistoryScheduleId] = useState<
    string | null
  >(null);

  const hasCreatePermission = sessionHelper.hasRole(
    PermittedRoles.REPORT_SCHEDULER?.CREATE_ROLES,
  );
  const hasUpdatePermission = sessionHelper.hasRole(
    PermittedRoles.REPORT_SCHEDULER?.UPDATE_ROLES,
  );
  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.REPORT_SCHEDULER?.DELETE_ROLES,
  );
  const hasTogglePermission = sessionHelper.hasRole(
    PermittedRoles.REPORT_SCHEDULER?.TOGGLE_ROLES,
  );
  const hasTestPermission = sessionHelper.hasRole(
    PermittedRoles.REPORT_SCHEDULER?.TEST_ROLES,
  );
  const hasRunHistoryPermission = sessionHelper.hasRole(
    PermittedRoles.REPORT_SCHEDULER?.RUN_HISTORY_ROLES,
  );

  const fetchSchedules = async (
    pageNum = 0,
    pageSize = 25,
    reportType = "",
  ) => {
    try {
      setLoading(true);
      const params: any = {
        pageNo: pageNum + 1,
        pageSize: pageSize,
      };
      if (reportType) params.reportType = reportType;

      const response = await listSchedules(params);

      if (response?.status && response?.data) {
        setSchedules(response.data.data || []);
        setTotalSchedules(response.data.total || 0);
      }
    } catch (error: any) {
      console.error("Error fetching report schedules:", error);
      showSnackbar(
        error?.message || "Failed to fetch report schedules",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules(page, rowsPerPage, reportTypeFilter);
  }, []);

  const handleSearchChange = (value: any) => {
    setSearch(value);
  };

  const handleReportTypeChange = (event: any) => {
    const newType = event.target.value;
    setReportTypeFilter(newType);
    setPage(0);
    fetchSchedules(0, rowsPerPage, newType);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
    fetchSchedules(newPage, rowsPerPage, reportTypeFilter);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchSchedules(0, newRowsPerPage, reportTypeFilter);
  };

  const editSchedulePath = (scheduleId: string) =>
    (configData.routes as any).reportSchedulers.edit?.replace(
      ":id",
      scheduleId,
    );

  const handleEditSchedule = (scheduleId: string) => {
    navigate(editSchedulePath(scheduleId));
  };

  const handleCreateSchedule = () => {
    navigate((configData.routes as any).reportSchedulers.create);
  };

  const handleToggleSchedule = async (scheduleId: string) => {
    try {
      const response = await toggleSchedule(scheduleId);
      if (response?.status) {
        showSnackbar("Schedule status updated successfully", "success");
        fetchSchedules(page, rowsPerPage, reportTypeFilter);
      }
    } catch (error: any) {
      console.error("Error toggling schedule:", error);
      showSnackbar(
        error?.message || "Failed to toggle schedule status",
        "error",
      );
    }
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    updateDetails({ id: scheduleId, itemName: "this report schedule" });
    openConsent();
  };

  const confirmDelete = async () => {
    closeConsent();
    try {
      const response = await deleteSchedule(constentDetails.id);
      if (response?.status) {
        showSnackbar("Schedule deleted successfully", "success");
        fetchSchedules(page, rowsPerPage, reportTypeFilter);
      }
    } catch (error: any) {
      console.error("Error deleting schedule:", error);
      showSnackbar(error?.message || "Failed to delete schedule", "error");
    }
  };

  const handleOpenTestModal = (scheduleId: string) => {
    setTestScheduleId(scheduleId);
    setTestModalOpen(true);
  };

  const handleOpenRunHistoryModal = (scheduleId: string) => {
    setRunHistoryScheduleId(scheduleId);
    setRunHistoryModalOpen(true);
  };

  // Client-side filtering by operator name search
  const filteredSchedules = search
    ? schedules.filter((schedule: any) =>
        schedule.operatorId?.operatorName
          ?.toLowerCase()
          .includes(search.toLowerCase()),
      )
    : schedules;

  const hasAnyActionPermission =
    hasUpdatePermission ||
    hasDeletePermission ||
    hasTogglePermission ||
    hasTestPermission ||
    hasRunHistoryPermission;

  return (
    <ContentShell
      title="Report Schedulers"
      loading={loading}
      actions={
        <Box
          style={{
            display: "flex",
            flexFlow: "row nowrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          <SearchInput
            value={search}
            placeholder="Search by operator name"
            onSearch={handleSearchChange}
          />
          <FormControl size="small" style={{ minWidth: 160 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportTypeFilter}
              label="Report Type"
              onChange={handleReportTypeChange}
            >
              {REPORT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasCreatePermission && (
            <CustomButton onClick={handleCreateSchedule}>
              Create Schedule
            </CustomButton>
          )}
        </Box>
      }
    >
      <Box className={classes.container}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell className={classes.tableHeader}>
                  Operator Name
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  Report Type
                </TableCell>
                <TableCell className={classes.tableHeader}>Frequency</TableCell>
                <TableCell className={classes.tableHeader}>
                  Recipient Emails
                </TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  Status
                </TableCell>
                <TableCell className={classes.tableHeader}>Last Run</TableCell>
                {hasAnyActionPermission && (
                  <TableCell className={classes.tableHeader} align="center">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={hasAnyActionPermission ? 7 : 6}
                    align="center"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredSchedules.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={hasAnyActionPermission ? 7 : 6}
                    align="center"
                  >
                    No report schedules found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSchedules.map((schedule: any) => (
                  <TableRow
                    key={schedule._id}
                    hover
                    className={
                      hasUpdatePermission ? classes.clickableRow : undefined
                    }
                    style={{
                      cursor: hasUpdatePermission ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      if (!hasUpdatePermission) return;
                      navigateFromRow(
                        e,
                        navigate,
                        editSchedulePath(schedule._id),
                      );
                    }}
                  >
                    <TableCell>
                      {hasUpdatePermission ? (
                        <a
                          href={`#${editSchedulePath(schedule._id)}`}
                          className={classes.rowLink}
                        >
                          {schedule.operatorId?.operatorName || "-"}
                        </a>
                      ) : (
                        schedule.operatorId?.operatorName || "-"
                      )}
                    </TableCell>
                    <TableCell>{formatReportType(schedule.reports)}</TableCell>
                    <TableCell>{formatFrequency(schedule)}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={schedule.recipientEmails?.join(", ") || ""}
                        placement="top"
                      >
                        <span>{formatEmails(schedule.recipientEmails)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={schedule.isActive ? "Active" : "Disabled"}
                        className={
                          schedule.isActive
                            ? classes.activeChip
                            : classes.inactiveChip
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatLastRun(schedule.lastRunAt)}</TableCell>
                    {hasAnyActionPermission && (
                      <TableCell align="center">
                        <Box
                          style={{
                            display: "flex",
                            flexFlow: "row nowrap",
                            justifyContent: "center",
                            gap: 2,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {hasUpdatePermission && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditSchedule(schedule._id)}
                                aria-label="edit schedule"
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasTogglePermission && (
                            <Tooltip
                              title={
                                schedule.isActive
                                  ? "Pause Schedule"
                                  : "Activate Schedule"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleToggleSchedule(schedule._id)
                                }
                                aria-label="toggle schedule"
                                color={
                                  schedule.isActive ? "warning" : "success"
                                }
                              >
                                {schedule.isActive ? (
                                  <PauseIcon />
                                ) : (
                                  <PlayArrowIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasTestPermission && (
                            <Tooltip title="Send Test Report">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenTestModal(schedule._id)
                                }
                                aria-label="test schedule"
                                color="info"
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasRunHistoryPermission && (
                            <Tooltip title="Run History">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenRunHistoryModal(schedule._id)
                                }
                                aria-label="run history"
                                color="default"
                              >
                                <HistoryIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasDeletePermission && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteSchedule(schedule._id)
                                }
                                aria-label="delete schedule"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalSchedules}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      <RunHistoryModal
        open={runHistoryModalOpen}
        scheduleId={runHistoryScheduleId}
        onClose={() => {
          setRunHistoryModalOpen(false);
          setRunHistoryScheduleId(null);
        }}
      />
      <TestReportModal
        open={testModalOpen}
        scheduleId={testScheduleId}
        onClose={() => {
          setTestModalOpen(false);
          setTestScheduleId(null);
        }}
      />
      <DeleteConsentDialog
        open={isOpenConsent}
        onClose={closeConsent}
        onConfirm={confirmDelete}
        itemName={constentDetails?.itemName}
      />
    </ContentShell>
  );
};

export default ReportSchedulers;
